/**
 * Storage Helpers for RAE Project Management
 * Handles file operations for task files.
 * Currently operates in mock mode (data URLs); ready for Supabase Storage integration.
 */
import { TaskFile, TaskFileType } from '../types';
import { generateTempId } from '../utils/format';
import { supabase, isSupabaseConfigured } from './supabaseClient';
import { createLogger } from '../utils/logger';
import { FILE_SIZE_LIMITS, ALLOWED_MIME_TYPES } from '../constants';

const logger = createLogger('StorageHelpers');

// ==================== FILE VALIDATION ====================

export interface FileValidationError {
  file: string;
  reason: string;
}

export function validateFile(file: File, fileType: TaskFileType): FileValidationError | null {
  const isPhoto =
    fileType === 'work_photo' ||
    fileType === 'status_photo' ||
    fileType === 'comment_photo';

  if (isPhoto) {
    if (!ALLOWED_MIME_TYPES.IMAGE.includes(file.type))
      return { file: file.name, reason: 'Tipo de imagen no soportado. Permitidos: JPG, PNG, GIF, WebP.' };
    if (file.size > FILE_SIZE_LIMITS.IMAGE)
      return { file: file.name, reason: `Imagen demasiado grande (${(file.size / 1024 / 1024).toFixed(1)} MB). Máximo 5 MB.` };
  } else {
    const all = [...ALLOWED_MIME_TYPES.IMAGE, ...ALLOWED_MIME_TYPES.DOCUMENT];
    if (!all.includes(file.type))
      return { file: file.name, reason: 'Tipo no soportado. Permitidos: imágenes y PDF.' };
    if (file.size > FILE_SIZE_LIMITS.DOCUMENT)
      return { file: file.name, reason: `Archivo demasiado grande (${(file.size / 1024 / 1024).toFixed(1)} MB). Máximo 10 MB.` };
  }
  return null;
}

// ==================== RETRY HELPER ====================

export async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries = 2,
  delayMs = 1000,
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (err: unknown) {
      lastError = err;
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes('validation') || attempt === maxRetries) break;
      const wait = delayMs * Math.pow(2, attempt);
      logger.warn(`[RETRY] Intento ${attempt + 1} fallido, reintentando en ${wait}ms...`, message);
      await new Promise(r => setTimeout(r, wait));
    }
  }
  throw lastError;
}

// ==================== FILE TYPE LABELS ====================

const TASK_FILE_TYPE_LABELS: Record<TaskFileType, string> = {
  work_photo:    'Work_Photo',
  document:      'Document',
  status_photo:  'Status_Photo',
  comment_photo: 'Comment_Photo',
};

// ==================== FILE SIZE FORMATTING ====================

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

// ==================== INTERNAL HELPERS ====================

/**
 * Generates a structured, timestamped filename regardless of the original name.
 * Format: {TypeLabel}_{base36-timestamp}_{4-char-random}.{ext}
 * Example: Work_Photo_lktj3a_8f2d.jpg
 */
function generateStructuredFileName(fileType: TaskFileType, originalName: string): string {
  const ext = (originalName.split('.').pop() || 'bin').toLowerCase();
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).substring(2, 6);
  const label = TASK_FILE_TYPE_LABELS[fileType] || 'File';
  return `${label}_${ts}_${rand}.${ext}`;
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ==================== MOCK: Create TaskFile from File ====================

export async function createMockTaskFile(
  file: File,
  subElementId: string,
  fileType: TaskFileType,
  uploadedBy: string,
): Promise<TaskFile> {
  const dataUrl = await fileToDataUrl(file);
  const structuredName = generateStructuredFileName(fileType, file.name);

  return {
    id: generateTempId('tf'),
    subElementId,
    fileType,
    fileName: structuredName,
    storagePath: `mock/${subElementId}/${structuredName}`,
    mimeType: file.type,
    fileSize: file.size,
    uploadedBy,
    createdAt: new Date().toISOString(),
    publicUrl: dataUrl,
  };
}

// ==================== SUPABASE: Upload TaskFile ====================

/**
 * Uploads a file to Supabase Storage and inserts a record into task_files.
 * Falls back to createMockTaskFile when Supabase is not configured.
 */
export async function uploadTaskFile(
  file: File,
  subElementId: string,
  fileType: TaskFileType,
  uploadedBy: string,
  commentId?: string,
): Promise<TaskFile> {
  logger.debug(`[UPLOAD] uploadTaskFile: file="${file.name}" size=${file.size} type=${fileType} subElement=${subElementId.slice(0,8)} commentId=${commentId?.slice(0,8) || 'none'}`);
  if (!isSupabaseConfigured()) {
    return createMockTaskFile(file, subElementId, fileType, uploadedBy);
  }

  const structuredName = generateStructuredFileName(fileType, file.name);
  const storagePath = `${subElementId}/${structuredName}`;
  logger.debug(`[UPLOAD] Uploading to storage: path=${storagePath}`);

  const { error: uploadError } = await supabase.storage
    .from('task-files')
    .upload(storagePath, file, { cacheControl: '3600', upsert: false });
  if (uploadError) {
    logger.error(`[UPLOAD] Storage upload FAILED`, uploadError);
    throw new Error(`Storage upload failed: ${uploadError.message}`);
  }
  logger.debug(`[UPLOAD] Storage upload SUCCESS`);

  const { data: { publicUrl } } = supabase.storage
    .from('task-files')
    .getPublicUrl(storagePath);
  logger.debug(`[UPLOAD] publicUrl=${publicUrl.slice(0,80)}`);

  logger.debug(`[UPLOAD] Inserting task_files record...`);
  const { data, error: dbError } = await supabase
    .from('task_files')
    .insert({
      sub_element_id: subElementId,
      comment_id: commentId || null,
      file_type: fileType,
      file_name: structuredName,
      storage_path: storagePath,
      mime_type: file.type,
      file_size: file.size,
      uploaded_by: uploadedBy,
    })
    .select()
    .single();
  if (dbError) {
    logger.error(`[UPLOAD] DB insert FAILED`, dbError);
    throw new Error(`DB insert failed: ${dbError.message}`);
  }
  logger.debug(`[UPLOAD] DB insert SUCCESS: id=${data.id.slice(0,8)} created_at=${data.created_at}`);

  return {
    id: data.id,
    subElementId,
    commentId: commentId || undefined,
    fileType,
    fileName: structuredName,
    storagePath,
    mimeType: file.type,
    fileSize: file.size,
    uploadedBy,
    createdAt: data.created_at,
    publicUrl,
  };
}
